// Header balance pill + tap-to-explain popover.
//
// Replaces the earlier `5/5 · 6✨` chip whose meaning was opaque: the
// sparkle suggested decoration, not currency, and the slash separator
// looked like a fraction unrelated to credits. Now the two counts are
// visually segmented (free vs paid) with a labelled popover the user
// can summon by tapping the pill \u2014 same UX as the DEMO badge.

import { createSignal, createEffect, onCleanup, Show } from "solid-js";
import { Icons } from "./Icons";
import { t } from "../lib/i18n";

interface Props {
  freeLeft: number;
  freeLimit: number;
  credits: number;
  /** Fires when the user taps the popover's CTA. */
  onBuy: () => void;
}

export function BalancePill(props: Props) {
  const [open, setOpen] = createSignal(false);
  let wrapRef: HTMLDivElement | undefined;

  // Outside-click / Escape closes \u2014 same pattern as DemoBadge.
  createEffect(() => {
    if (!open()) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    onCleanup(() => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    });
  });

  const empty = () =>
    props.freeLeft === 0 && props.credits === 0;

  return (
    <div ref={wrapRef} style={{ position: "relative", "justify-self": "start" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open()}
        aria-label={t("balance.aria")}
        style={{
          display: "inline-flex",
          "align-items": "center",
          gap: "0",
          padding: "0",
          "min-height": "30px",
          background: empty()
            ? "rgba(248, 219, 168, 0.55)"
            : "linear-gradient(135deg, #fff7e0 0%, #f7e8bf 100%)",
          border: empty()
            ? "1px solid rgba(184, 146, 63, 0.45)"
            : "1px solid rgba(184, 146, 63, 0.35)",
          "border-radius": "100px",
          cursor: "pointer",
          "font-family": "inherit",
          "box-shadow":
            "inset 0 1px 0 rgba(255,255,255,0.55), 0 1px 2px rgba(120,95,40,0.08)",
          overflow: "hidden",
        }}
      >
        {/* Free segment */}
        <span
          style={{
            display: "inline-flex",
            "align-items": "center",
            gap: "5px",
            padding: "5px 10px 5px 11px",
            "font-size": "12.5px",
            "font-weight": 700,
            color: props.freeLeft === 0 ? "#a89358" : "#5a4a20",
            "letter-spacing": "0.2px",
          }}
        >
          <Icons.Clock size={12} color={props.freeLeft === 0 ? "#a89358" : "#8a6f1f"} />
          {props.freeLeft}/{props.freeLimit}
        </span>
        {/* Separator + paid segment. Hidden when credits = 0 to keep the
            pill compact for free-only users. */}
        <Show when={props.credits > 0}>
          <span
            aria-hidden="true"
            style={{
              width: "1px",
              height: "16px",
              background: "rgba(184, 146, 63, 0.35)",
            }}
          />
          <span
            style={{
              display: "inline-flex",
              "align-items": "center",
              gap: "4px",
              padding: "5px 11px 5px 10px",
              "font-size": "12.5px",
              "font-weight": 700,
              color: "#5a4a20",
              "letter-spacing": "0.2px",
            }}
          >
            <Icons.Star size={12} color="#b8923f" />
            {props.credits}
          </span>
        </Show>
        <Show when={empty()}>
          <span
            style={{
              display: "inline-flex",
              "align-items": "center",
              "padding-right": "10px",
              color: "#8a6f1f",
              "font-size": "14px",
              "font-weight": 800,
              "margin-left": "-3px",
            }}
          >
            +
          </span>
        </Show>
      </button>

      <Show when={open()}>
        <Popover
          freeLeft={props.freeLeft}
          freeLimit={props.freeLimit}
          credits={props.credits}
          onBuy={() => {
            setOpen(false);
            props.onBuy();
          }}
        />
      </Show>
    </div>
  );
}

function Popover(p: {
  freeLeft: number;
  freeLimit: number;
  credits: number;
  onBuy: () => void;
}) {
  return (
    <div
      role="dialog"
      style={{
        position: "absolute",
        top: "calc(100% + 8px)",
        left: "0",
        "z-index": 80,
        "min-width": "240px",
        background: "#fffefb",
        border: "1px solid rgba(184, 146, 63, 0.35)",
        "border-radius": "16px",
        "box-shadow":
          "0 12px 30px rgba(120,95,40,0.22), 0 2px 6px rgba(120,95,40,0.1)",
        padding: "14px 14px 12px",
        animation: "fade-in 0.18s ease-out",
      }}
    >
      {/* Arrow pointer */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "-6px",
          left: "22px",
          width: "12px",
          height: "12px",
          background: "#fffefb",
          "border-top": "1px solid rgba(184, 146, 63, 0.35)",
          "border-left": "1px solid rgba(184, 146, 63, 0.35)",
          transform: "rotate(45deg)",
        }}
      />

      <Row
        icon={<Icons.Clock size={14} color="#8a6f1f" />}
        title={t("balance.freeTitle")}
        value={`${p.freeLeft}/${p.freeLimit}`}
        hint={t("balance.freeHint")}
      />
      <div
        style={{
          height: "1px",
          background: "rgba(184, 146, 63, 0.18)",
          margin: "10px 0",
        }}
      />
      <Row
        icon={<Icons.Star size={14} color="#b8923f" />}
        title={t("balance.paidTitle")}
        value={String(p.credits)}
        hint={t("balance.paidHint")}
      />

      <button
        type="button"
        onClick={p.onBuy}
        style={{
          "margin-top": "14px",
          width: "100%",
          padding: "10px 14px",
          background: "#1a1a1a",
          color: "#fff",
          border: "none",
          "border-radius": "100px",
          "font-family": "inherit",
          "font-size": "13px",
          "font-weight": 700,
          "letter-spacing": "0.3px",
          cursor: "pointer",
        }}
      >
        {t("balance.buy")}
      </button>
    </div>
  );
}

function Row(p: {
  icon: any;
  title: string;
  value: string;
  hint: string;
}) {
  return (
    <div style={{ display: "flex", "align-items": "flex-start", gap: "10px" }}>
      <span
        style={{
          width: "26px",
          height: "26px",
          "border-radius": "13px",
          background: "rgba(184, 146, 63, 0.14)",
          display: "inline-flex",
          "align-items": "center",
          "justify-content": "center",
          "flex-shrink": 0,
          "margin-top": "1px",
        }}
      >
        {p.icon}
      </span>
      <div style={{ flex: 1, "min-width": 0 }}>
        <div
          class="flex items-baseline justify-between"
          style={{ gap: "8px" }}
        >
          <div
            style={{
              "font-size": "12.5px",
              "font-weight": 700,
              color: "#1a1a1a",
              "letter-spacing": "0.1px",
            }}
          >
            {p.title}
          </div>
          <div
            class="serif"
            style={{
              "font-size": "16px",
              "font-weight": 500,
              color: "#1a1a1a",
            }}
          >
            {p.value}
          </div>
        </div>
        <div
          style={{
            "font-size": "11.5px",
            color: "#7a7a7a",
            "line-height": 1.35,
            "margin-top": "2px",
          }}
        >
          {p.hint}
        </div>
      </div>
    </div>
  );
}
